import { useEffect, useRef, useState } from 'react';

import type { ActiveJobDto, JobDetailDto } from '@chat-emp/shared';
import { getActiveJobs, getJobDetail } from '../lib/facade.js';
import { matchesActiveJob } from '../data/jobs.js';
import { Button } from '../components/Button.js';
import { EmptyState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { useDismiss } from '../util/useDismiss.js';
import { JobDetailsDialog, type JobDetailsSource } from './JobDetailsDialog.js';
import styles from './JobPickerDialog.module.css';

/** A detail, in the shape the read-only dialog reads. */
function detailsSourceOf(detail: JobDetailDto): JobDetailsSource {
  return {
    jobTitle: detail.title,
    jobType: detail.jobType,
    ...(detail.location ? { jobLocation: detail.location } : {}),
    ...(detail.description ? { description: detail.description } : {}),
    ...(detail.responsibilities ? { responsibilities: detail.responsibilities } : {}),
    ...(detail.requirements ? { requirements: detail.requirements } : {}),
  };
}

/* The company's live postings, filtered client-side; a pick's detail is fetched only on Details. */
export function JobPickerDialog({
  onClose,
  onSendJob,
  contained = false,
}: {
  onClose: () => void;
  /** Posts one message per posting. The dialog loops it, in pick order. */
  onSendJob: (jobId: string) => Promise<void>;
  /** Scope the modal to a floating panel (the widget) instead of the viewport. */
  contained?: boolean;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDismiss(true, onClose, dialogRef);

  const [jobs, setJobs] = useState<ActiveJobDto[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const [preview, setPreview] = useState<JobDetailsSource | null>(null);
  const [detailsLoadingId, setDetailsLoadingId] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    getActiveJobs()
      .then((rows) => live && setJobs(rows))
      .catch(() => live && setLoadError(true));
    return () => {
      live = false;
    };
  }, []);

  const toggle = (id: string) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const openDetails = async (id: string) => {
    setDetailsLoadingId(id);
    setSendError(null);
    try {
      setPreview(detailsSourceOf(await getJobDetail(id)));
    } catch {
      setSendError('Could not load that posting. Try again.');
    } finally {
      setDetailsLoadingId(null);
    }
  };

  const send = async () => {
    const ids = (jobs ?? []).filter((j) => picked.has(j.id)).map((j) => j.id);
    if (ids.length === 0) return;
    setSending(true);
    setSendError(null);
    try {
      for (const id of ids) await onSendJob(id);
      onClose();
    } catch {
      setSendError('Could not send that posting. Try again.');
    } finally {
      setSending(false);
    }
  };

  // Ticked rows survive a non-matching search, so a selection isn't hidden by the filter.
  const visible = (jobs ?? []).filter((j) => matchesActiveJob(j, query) || picked.has(j.id));
  const count = picked.size;
  const sendLabel = count > 1 ? `Send ${count} jobs` : 'Send job';

  return (
    <div className={contained ? styles.overlayContained : styles.overlay}>
      <div
        className={contained ? `${styles.dialog} ${styles.contained}` : styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label="Send job details"
        ref={dialogRef}
      >
        <div className={styles.header}>
          <span className={styles.iconBadge}>
            <Icon name="briefcase" size={18} />
          </span>
          <div className={styles.heading}>
            <h2 className={styles.title}>Send job details</h2>
            <p className={styles.subtitle}>
              Pick one or several. Each lands in this chat as a card.
            </p>
          </div>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            <Icon name="close" size={20} />
          </button>
        </div>

        {loadError ? (
          <div className={styles.empty}>
            <EmptyState
              icon={<Icon name="briefcase" size={32} />}
              title="Couldn't load your jobs"
              description="Close this and try again in a moment."
            />
          </div>
        ) : jobs === null ? (
          <p className={styles.note}>Loading your jobs…</p>
        ) : jobs.length === 0 ? (
          <div className={styles.empty}>
            <EmptyState
              icon={<Icon name="briefcase" size={32} />}
              title="No active jobs"
              description="Postings you publish will show up here."
            />
          </div>
        ) : (
          <>
            <div className={styles.toolbar}>
              <label className={styles.search}>
                <Icon name="search" size={16} />
                <input
                  type="search"
                  className={styles.searchInput}
                  placeholder="Search by title or location"
                  value={query}
                  autoFocus
                  onChange={(event) => setQuery(event.target.value)}
                />
              </label>
            </div>

            {visible.length === 0 ? (
              <p className={styles.note}>No jobs match “{query}”.</p>
            ) : (
              <ul className={styles.list}>
                {visible.map((job) => {
                  const on = picked.has(job.id);
                  const applicants = `${job.applicantCount} applicant${job.applicantCount === 1 ? '' : 's'}`;
                  const place = [job.location, applicants].filter(Boolean).join(' · ');
                  return (
                    <li key={job.id} className={on ? styles.rowOn : styles.row}>
                      <button
                        type="button"
                        className={styles.pick}
                        aria-pressed={on}
                        onClick={() => toggle(job.id)}
                      >
                        <span className={styles.tick}>{on && <Icon name="check" size={14} />}</span>
                        <span className={styles.text}>
                          <span className={styles.name}>{job.title}</span>
                          <span className={styles.meta}>{place}</span>
                          {job.jobType && <span className={styles.meta}>{job.jobType}</span>}
                        </span>
                      </button>
                      <button
                        type="button"
                        className={styles.details}
                        disabled={detailsLoadingId !== null}
                        onClick={() => void openDetails(job.id)}
                      >
                        {detailsLoadingId === job.id ? 'Loading…' : 'Details'}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}

        {sendError && <p className={styles.error}>{sendError}</p>}

        <div className={styles.footer}>
          {count > 0 && <span className={styles.count}>{count} selected</span>}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={count === 0 || sending} onClick={() => void send()}>
            {sending ? 'Sending…' : sendLabel}
          </Button>
        </div>
      </div>

      {preview && <JobDetailsDialog job={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
